import { CustomFormApi } from 'informed';
import {
  Dispatch,
  MutableRefObject,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { RegisterForm } from '@/api/auth';
import { FormError, replaceErrorField } from '@/components/form/utils';
import { replaceErrorFieldMap } from '@/helpers/auth';
import { showTooltip } from '@/helpers/tooltip';
import { useNavigation } from '@/hooks/navigation';
import { sendRegFormSubmitEvent } from '@/modules/googleAnalytics';
import { TooltipTypes } from '@/store/tooltipStore';

import { ScErrorLink } from '../styled';

interface UseErrorValidationParams {
  initialValues: RegisterForm;
  registerError: any;
  loginError: any;
  isAuthRequestsPending: boolean;
  setIsAuthRequestsPending: Dispatch<SetStateAction<boolean>>;
  isOneStepRegistration?: boolean;
  formApiRef: MutableRefObject<CustomFormApi<RegisterForm> | undefined>;
}

export const useErrorValidation = ({
  initialValues,
  registerError,
  loginError,
  isAuthRequestsPending,
  setIsAuthRequestsPending,
  isOneStepRegistration,
  formApiRef,
}: UseErrorValidationParams) => {
  const { navigate } = useNavigation();
  const { t } = useTranslation('common');

  const handleOpenLoginModal = useCallback(
    () => navigate('/?modal=login'),
    [navigate]
  );

  const handleOpenRecoveryModal = useCallback(() => {
    const formValues = formApiRef.current?.getFormState().values;

    navigate(`/?modal=recovery&email=${formValues?.email}`);
  }, [navigate, formApiRef]);

  const errors = useMemo(() => {
    if (isAuthRequestsPending) return [];

    let formErrors: FormError[] = [];

    formErrors = formErrors.concat(mapErrorObject(registerError?.errors));
    formErrors = formErrors.concat(
      mapErrorObject(registerError?.errors?.profile)
    );

    if (loginError?.password?.wrongPassword) {
      formErrors.push({
        field: 'email',
        message: (
          <Trans
            i18nKey={
              isOneStepRegistration
                ? 'auth.registration.form.emailTakenOneStep'
                : 'auth.registration.form.emailTaken'
            }
            components={{
              1: <ScErrorLink onClick={handleOpenLoginModal} />,
              2: <ScErrorLink onClick={handleOpenRecoveryModal} />,
            }}
          />
        ),
      });
    }

    return replaceErrorField(formErrors, replaceErrorFieldMap);
  }, [
    registerError,
    loginError,
    isAuthRequestsPending,
    isOneStepRegistration,
    handleOpenLoginModal,
    handleOpenRecoveryModal,
  ]);

  useEffect(() => {
    if (formApiRef.current == null || errors.length === 0) return;

    errors.forEach(({ field, message }) => {
      formApiRef.current?.setError(field, message);
    });
  }, [errors, formApiRef]);

  const getLoginValues = useCallback(() => {
    const formValues = formApiRef.current?.getFormState().values;
    if (!formValues) return;

    return {
      email: formValues?.email || initialValues.email,
      password: formValues?.password || initialValues.password,
    };
  }, [formApiRef]);

  useEffect(() => {
    if (!loginError) return;

    setIsAuthRequestsPending(false);
    sendRegFormSubmitEvent({
      status: 'failed',
      action: 'login',
      method: 'email',
      request_data: getLoginValues(),
      error_data: loginError,
    });

    if (!loginError.password?.wrongPassword) {
      showTooltip({
        type: TooltipTypes.ERROR,
        body: t('errorUnknown'),
      });
    }
  }, [loginError, getLoginValues, setIsAuthRequestsPending, t]);
};

const mapErrorObject = (
  errorObj: { [key: string]: any } | undefined
): FormError[] => {
  if (!errorObj) {
    return [];
  }

  return Object.keys(errorObj).map((key) => ({
    field: key,
    message: Object.values(errorObj[key]),
  }));
};
